/**
 * ╔══════════════════════════════════════════════════════════════════════════════╗
 * ║ File: resources/assets/frontend/js/app.js                                   ║
 * ║ Purpose: Bootstrap the SalesPartner public frontend with Alpine.js,         ║
 * ║          accessible navigation, resilient theme persistence, Lucide icon    ║
 * ║          hydration, smooth scrolling, counters, and marketing-page UI       ║
 * ║          behavior.                                                          ║
 * ║ Author: Jürgen Schmitt | BSBV Inc.                                          ║
 * ║ License: Proprietary © BSBV Inc. 2026                                       ║
 * ║ Framework: Alpine.js 3.x                                                    ║
 * ║ Status: Production-Ready                                                    ║
 * ╚══════════════════════════════════════════════════════════════════════════════╝
 */

import Alpine from '@alpinejs/csp';
import collapse from '@alpinejs/collapse';
import focus from '@alpinejs/focus';
import persist from '@alpinejs/persist';
import {
    ArrowRight,
    ArrowUpRight,
    BadgeCheck,
    Briefcase,
    Building2,
    CalendarDays,
    Check,
    ChevronDown,
    ChevronRight,
    Clock3,
    createIcons,
    Globe,
    Handshake,
    Mail,
    MapPin,
    Megaphone,
    Menu,
    MessageSquare,
    MonitorSmartphone,
    MoonStar,
    MoveRight,
    Phone,
    Play,
    Search,
    ShieldCheck,
    SunMedium,
    TrendingUp,
    X,
} from 'lucide';

const BRAND = Object.freeze({
    appName: 'SalesPartner',
    namespace: 'salespartner',
    year: '2026',
});

const STORAGE_KEYS = Object.freeze({
    themePreference: `${BRAND.namespace}:theme-preference`,
});

const SELECTORS = Object.freeze({
    navLinks: '[data-nav-link]',
    navSections: '[data-nav-section]',
    smoothAnchor: 'a[href^="#"]:not([href="#"])',
    counter: '[data-counter-value]',
    progress: '[data-progress]',
    currentYear: '[data-current-year]',
    reveal: '[data-reveal]',
    formValidate: '[data-form-validate]',
    icon: '[data-lucide]',
});

const THEME = Object.freeze({
    light: 'light',
    dark: 'dark',
    system: 'system',
});

const ICONS = Object.freeze({
    ArrowRight,
    ArrowUpRight,
    BadgeCheck,
    Briefcase,
    Building2,
    CalendarDays,
    Check,
    ChevronDown,
    ChevronRight,
    Clock3,
    Globe,
    Handshake,
    Mail,
    MapPin,
    Megaphone,
    Menu,
    MessageSquare,
    MonitorSmartphone,
    MoonStar,
    MoveRight,
    Phone,
    Play,
    Search,
    ShieldCheck,
    SunMedium,
    TrendingUp,
    X,
});

const supportsMatchMedia = typeof window.matchMedia === 'function';

const createMediaQuery = (query, fallbackMatches = false) => {
    if (supportsMatchMedia) {
        return window.matchMedia(query);
    }

    return {
        matches: fallbackMatches,
        media: query,
        addEventListener() {},
        removeEventListener() {},
        addListener() {},
        removeListener() {},
    };
};

const media = Object.freeze({
    darkScheme: createMediaQuery('(prefers-color-scheme: dark)'),
    reducedMotion: createMediaQuery('(prefers-reduced-motion: reduce)'),
    desktop: createMediaQuery('(min-width: 64rem)'),
});

const logger = {
    error(error, context = 'Unhandled frontend error') {
        const detail = {
            context,
            message: error instanceof Error ? error.message : String(error),
            stack: error instanceof Error ? error.stack : null,
        };

        console.error(`[${BRAND.appName}] ${context}`, error);
        document.dispatchEvent(new CustomEvent(`${BRAND.namespace}:error`, { detail }));
    },
};

const safeStorage = {
    get(key) {
        try {
            return window.localStorage.getItem(key);
        } catch {
            return null;
        }
    },

    set(key, value) {
        try {
            window.localStorage.setItem(key, value);
            return true;
        } catch {
            return false;
        }
    },

    remove(key) {
        try {
            window.localStorage.removeItem(key);
            return true;
        } catch {
            return false;
        }
    },
};

const dom = {
    query(selector, scope = document) {
        return scope.querySelector(selector);
    },

    queryAll(selector, scope = document) {
        return Array.from(scope.querySelectorAll(selector));
    },
};

const bindMediaChange = (mediaQuery, listener) => {
    if (!mediaQuery || typeof listener !== 'function') {
        return () => {};
    }

    if (typeof mediaQuery.addEventListener === 'function') {
        mediaQuery.addEventListener('change', listener);
        return () => mediaQuery.removeEventListener('change', listener);
    }

    if (typeof mediaQuery.addListener === 'function') {
        mediaQuery.addListener(listener);
        return () => mediaQuery.removeListener(listener);
    }

    return () => {};
};

const bodyScrollLock = {
    depth: 0,
    previousOverflow: '',
    previousPaddingInlineEnd: '',

    lock() {
        this.depth += 1;

        if (this.depth > 1) {
            return;
        }

        const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);

        this.previousOverflow = document.documentElement.style.overflow;
        this.previousPaddingInlineEnd = document.body.style.paddingInlineEnd;

        document.documentElement.style.overflow = 'hidden';

        if (scrollbarWidth > 0) {
            document.body.style.paddingInlineEnd = `${scrollbarWidth}px`;
        }
    },

    unlock(force = false) {
        this.depth = force ? 0 : Math.max(0, this.depth - 1);

        if (this.depth > 0) {
            return;
        }

        document.documentElement.style.overflow = this.previousOverflow;
        document.body.style.paddingInlineEnd = this.previousPaddingInlineEnd;
    },
};

const parseNumber = (value) => {
    const normalized = String(value ?? '')
        .trim()
        .replace(/[^\d.,-]/g, '')
        .replace(/,(?=\d{3}(\D|$))/g, '')
        .replace(',', '.');

    const numericValue = Number.parseFloat(normalized);

    return Number.isFinite(numericValue) ? numericValue : 0;
};

const getHeaderOffset = () => {
    const rootStyles = window.getComputedStyle(document.documentElement);
    const rawValue = rootStyles.getPropertyValue('--header-offset').trim();
    const fontSize = Number.parseFloat(rootStyles.fontSize || '16');
    const parsed = Number.parseFloat(rawValue);

    if (!Number.isFinite(parsed)) {
        return 80;
    }

    if (rawValue.endsWith('rem')) {
        return parsed * fontSize;
    }

    return parsed;
};

const getAnchorTarget = (href) => {
    if (!href || !href.startsWith('#')) {
        return null;
    }

    const id = href.slice(1);

    if (!id) {
        return null;
    }

    return document.getElementById(id);
};

const scrollToTarget = (target, options = {}) => {
    if (!(target instanceof HTMLElement)) {
        return false;
    }

    const { updateHistory = true, focusTarget = true } = options;
    const yOffset = getHeaderOffset() + 16;
    const targetY = window.scrollY + target.getBoundingClientRect().top - yOffset;
    const behavior = media.reducedMotion.matches ? 'auto' : 'smooth';

    window.scrollTo({
        top: Math.max(0, targetY),
        behavior,
    });

    if (focusTarget) {
        window.setTimeout(() => {
            if (!target.hasAttribute('tabindex')) {
                target.setAttribute('tabindex', '-1');
            }

            target.focus({ preventScroll: true });
        }, media.reducedMotion.matches ? 0 : 250);
    }

    if (updateHistory && target.id) {
        window.history.pushState({}, '', `#${target.id}`);
    }

    return true;
};

const setAriaCurrent = (sectionId) => {
    const links = dom.queryAll(SELECTORS.navLinks);

    links.forEach((link) => {
        const href = link.getAttribute('href') || '';
        const targetSection = link.getAttribute('data-nav-section-target') || '';
        const isActive = href === `#${sectionId}` || targetSection === sectionId || href.endsWith(`#${sectionId}`);

        if (isActive) {
            link.setAttribute('aria-current', 'page');
            link.classList.add('is-active');
            return;
        }

        link.removeAttribute('aria-current');
        link.classList.remove('is-active');
    });
};

const getSavedThemePreference = () => {
    const stored = safeStorage.get(STORAGE_KEYS.themePreference);

    if ([THEME.light, THEME.dark, THEME.system].includes(stored)) {
        return stored;
    }

    return THEME.system;
};

const resolveTheme = (preference) => {
    if (preference === THEME.dark) {
        return THEME.dark;
    }

    if (preference === THEME.light) {
        return THEME.light;
    }

    return media.darkScheme.matches ? THEME.dark : THEME.light;
};

const applyThemePreference = (preference) => {
    const html = document.documentElement;
    const resolvedTheme = resolveTheme(preference);

    html.classList.remove(THEME.light, THEME.dark);

    if (preference === THEME.light) {
        html.classList.add(THEME.light);
    }

    if (preference === THEME.dark) {
        html.classList.add(THEME.dark);
    }

    html.dataset.themePreference = preference;
    html.dataset.theme = resolvedTheme;
    html.style.colorScheme = resolvedTheme;

    document.dispatchEvent(new CustomEvent(`${BRAND.namespace}:theme-changed`, {
        detail: { preference, resolvedTheme },
    }));
};

let iconFrame = 0;
let iconObserver = null;

const refreshIcons = () => {
    window.cancelAnimationFrame(iconFrame);

    iconFrame = window.requestAnimationFrame(() => {
        try {
            createIcons({
                icons: ICONS,
                nameAttr: 'data-lucide',
                attrs: {
                    class: 'lucide',
                    'aria-hidden': 'true',
                    focusable: 'false',
                },
            });
        } catch (error) {
            logger.error(error, 'Lucide icon hydration failed');
        }
    });
};

const observeDynamicIcons = () => {
    if (!(document.body instanceof HTMLElement)) {
        return;
    }

    if (iconObserver instanceof MutationObserver) {
        iconObserver.disconnect();
    }

    iconObserver = new MutationObserver((mutations) => {
        const needsRefresh = mutations.some((mutation) => {
            if (mutation.type === 'attributes') {
                return mutation.attributeName === 'data-lucide';
            }

            return Array.from(mutation.addedNodes).some((node) => {
                return node instanceof HTMLElement && (
                    node.matches?.(SELECTORS.icon) ||
                    node.querySelector?.(SELECTORS.icon)
                );
            });
        });

        if (needsRefresh) {
            refreshIcons();
        }
    });

    iconObserver.observe(document.body, {
        subtree: true,
        childList: true,
        attributes: true,
        attributeFilter: ['data-lucide'],
    });
};

const bindSmoothAnchors = () => {
    document.addEventListener('click', (event) => {
        const anchor = event.target instanceof Element ? event.target.closest(SELECTORS.smoothAnchor) : null;

        if (!(anchor instanceof HTMLAnchorElement)) {
            return;
        }

        const href = anchor.getAttribute('href') || '';
        const target = getAnchorTarget(href);

        if (!(target instanceof HTMLElement)) {
            return;
        }

        event.preventDefault();
        scrollToTarget(target);
    });
};

const bindCurrentYear = () => {
    const year = new Date().getFullYear().toString();

    dom.queryAll(SELECTORS.currentYear).forEach((node) => {
        node.textContent = year;
    });
};

const bindProgressBars = () => {
    dom.queryAll(SELECTORS.progress).forEach((node) => {
        const value = parseNumber(node.getAttribute('data-progress'));
        const clamped = Math.min(100, Math.max(0, value));

        node.style.setProperty('--progress', `${clamped}%`);
        node.setAttribute('aria-valuemin', '0');
        node.setAttribute('aria-valuemax', '100');
        node.setAttribute('aria-valuenow', String(Math.round(clamped)));
    });
};

const animateCounter = (node) => {
    if (!(node instanceof HTMLElement) || node.dataset.counterAnimated === 'true') {
        return;
    }

    const targetValue = parseNumber(node.getAttribute('data-counter-value') || node.textContent || '0');
    const decimals = Number.parseInt(node.getAttribute('data-counter-decimals') || '0', 10);
    const duration = Math.max(200, Number.parseInt(node.getAttribute('data-counter-duration') || '1400', 10));
    const prefix = node.getAttribute('data-counter-prefix') || '';
    const suffix = node.getAttribute('data-counter-suffix') || '';
    const locale = document.documentElement.lang || 'en';
    const formatter = new Intl.NumberFormat(locale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });

    node.dataset.counterAnimated = 'true';

    if (media.reducedMotion.matches) {
        node.textContent = `${prefix}${formatter.format(targetValue)}${suffix}`;
        return;
    }

    const start = window.performance.now();
    const step = (now) => {
        const progress = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - progress, 3);
        const currentValue = targetValue * eased;

        node.textContent = `${prefix}${formatter.format(currentValue)}${suffix}`;

        if (progress < 1) {
            window.requestAnimationFrame(step);
            return;
        }

        node.textContent = `${prefix}${formatter.format(targetValue)}${suffix}`;
    };

    window.requestAnimationFrame(step);
};

const bindCounters = () => {
    const counters = dom.queryAll(SELECTORS.counter);

    if (counters.length === 0) {
        return;
    }

    if (media.reducedMotion.matches || !('IntersectionObserver' in window)) {
        counters.forEach(animateCounter);
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }

            animateCounter(entry.target);
            observer.unobserve(entry.target);
        });
    }, {
        threshold: 0.45,
        rootMargin: '0px 0px -8% 0px',
    });

    counters.forEach((counter) => {
        observer.observe(counter);
    });
};

const bindRevealObserver = () => {
    const targets = dom.queryAll(SELECTORS.reveal);

    if (targets.length === 0) {
        return;
    }

    if (media.reducedMotion.matches || !('IntersectionObserver' in window)) {
        targets.forEach((node) => {
            node.dataset.revealState = 'visible';
        });
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        entries.forEach((entry) => {
            if (!entry.isIntersecting) {
                return;
            }

            entry.target.dataset.revealState = 'visible';
            observer.unobserve(entry.target);
        });
    }, {
        threshold: 0.18,
        rootMargin: '0px 0px -6% 0px',
    });

    targets.forEach((node) => {
        node.dataset.revealState = 'hidden';
        observer.observe(node);
    });
};

const bindSectionTracking = () => {
    const sections = dom.queryAll(SELECTORS.navSections).filter((section) => section.id);

    if (sections.length === 0 || !('IntersectionObserver' in window)) {
        return;
    }

    const observer = new IntersectionObserver((entries) => {
        const activeEntry = entries
            .filter((entry) => entry.isIntersecting)
            .sort((left, right) => right.intersectionRatio - left.intersectionRatio)[0];

        if (activeEntry?.target?.id) {
            setAriaCurrent(activeEntry.target.id);
        }
    }, {
        threshold: [0.2, 0.4, 0.6],
        rootMargin: `-${getHeaderOffset()}px 0px -45% 0px`,
    });

    sections.forEach((section) => {
        observer.observe(section);
    });

    const initialHash = window.location.hash.replace('#', '');

    if (initialHash) {
        setAriaCurrent(initialHash);
        return;
    }

    if (sections[0]) {
        setAriaCurrent(sections[0].id);
    }
};

const bindFormValidation = () => {
    dom.queryAll(SELECTORS.formValidate).forEach((form) => {
        if (!(form instanceof HTMLFormElement)) {
            return;
        }

        form.setAttribute('novalidate', 'novalidate');

        form.addEventListener('submit', (event) => {
            if (form.checkValidity()) {
                return;
            }

            event.preventDefault();

            const firstInvalid = form.querySelector(':invalid');

            if (firstInvalid instanceof HTMLElement) {
                firstInvalid.focus();
                firstInvalid.setAttribute('aria-invalid', 'true');
            }
        });

        form.addEventListener('input', (event) => {
            const field = event.target;

            if (!(field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement)) {
                return;
            }

            if (field.checkValidity()) {
                field.removeAttribute('aria-invalid');
            }
        });
    });
};

const bootStaticEnhancements = () => {
    document.documentElement.classList.add('js');
    bindSmoothAnchors();
    bindCurrentYear();
    bindProgressBars();
    bindCounters();
    bindRevealObserver();
    bindSectionTracking();
    bindFormValidation();
    refreshIcons();
    observeDynamicIcons();
};

const registerStores = () => {
    Alpine.store('app', {
        brand: BRAND.appName,
        year: BRAND.year,
        reducedMotion: media.reducedMotion.matches,
    });

    Alpine.store('theme', {
        preference: getSavedThemePreference(),
        unbindDarkScheme: null,

        init() {
            applyThemePreference(this.preference);

            this.unbindDarkScheme = bindMediaChange(media.darkScheme, () => {
                if (this.preference === THEME.system) {
                    applyThemePreference(THEME.system);
                }
            });
        },

        destroy() {
            this.unbindDarkScheme?.();
        },

        get resolved() {
            return resolveTheme(this.preference);
        },

        get isDark() {
            return this.resolved === THEME.dark;
        },

        set(preference) {
            if (![THEME.light, THEME.dark, THEME.system].includes(preference)) {
                return;
            }

            this.preference = preference;

            if (preference === THEME.system) {
                safeStorage.remove(STORAGE_KEYS.themePreference);
            } else {
                safeStorage.set(STORAGE_KEYS.themePreference, preference);
            }

            applyThemePreference(preference);
        },

        toggle() {
            this.set(this.resolved === THEME.dark ? THEME.light : THEME.dark);
        },
    });
};

const registerComponents = () => {
    Alpine.data('siteHeader', () => ({
        isOpen: false,
        isScrolled: false,
        activeSectionId: '',
        handleScroll: null,
        handleResize: null,
        handleEscape: null,
        removeThemeListener: null,

        init() {
            this.handleScroll = () => {
                this.isScrolled = window.scrollY > 12;
            };

            this.handleResize = () => {
                if (media.desktop.matches && this.isOpen) {
                    this.close();
                }
            };

            this.handleEscape = (event) => {
                if (event.key === 'Escape' && this.isOpen) {
                    this.close();
                }
            };

            this.handleScroll();
            window.addEventListener('scroll', this.handleScroll, { passive: true });
            window.addEventListener('resize', this.handleResize, { passive: true });
            document.addEventListener('keydown', this.handleEscape);

            if (window.location.hash) {
                this.activeSectionId = window.location.hash.slice(1);
            }

            this.removeThemeListener = () => {
                document.removeEventListener(`${BRAND.namespace}:theme-changed`, refreshIcons);
            };

            document.addEventListener(`${BRAND.namespace}:theme-changed`, refreshIcons);
        },

        destroy() {
            if (this.handleScroll) {
                window.removeEventListener('scroll', this.handleScroll);
            }

            if (this.handleResize) {
                window.removeEventListener('resize', this.handleResize);
            }

            if (this.handleEscape) {
                document.removeEventListener('keydown', this.handleEscape);
            }

            this.removeThemeListener?.();
            bodyScrollLock.unlock(true);
        },

        open() {
            this.isOpen = true;
            bodyScrollLock.lock();
        },

        close() {
            this.isOpen = false;
            bodyScrollLock.unlock();
        },

        toggle() {
            if (this.isOpen) {
                this.close();
                return;
            }

            this.open();
        },

        navigate(hash) {
            const target = getAnchorTarget(hash);

            if (target instanceof HTMLElement) {
                scrollToTarget(target);
                this.activeSectionId = target.id;
            }

            this.close();
        },
    }));

    Alpine.data('themeToggle', () => ({
        get preference() {
            return Alpine.store('theme').preference;
        },

        get resolved() {
            return Alpine.store('theme').resolved;
        },

        get isDark() {
            return Alpine.store('theme').isDark;
        },

        toggle() {
            Alpine.store('theme').toggle();
            refreshIcons();
        },

        set(preference) {
            Alpine.store('theme').set(preference);
            refreshIcons();
        },
    }));

    Alpine.data('metricCounter', (value = 0, options = {}) => ({
        value: 0,
        observer: null,
        target: parseNumber(value),
        decimals: Number.parseInt(options.decimals ?? 0, 10),
        duration: Number.parseInt(options.duration ?? 1400, 10),
        prefix: options.prefix ?? '',
        suffix: options.suffix ?? '',
        hasAnimated: false,

        init() {
            if (media.reducedMotion.matches) {
                this.value = this.target;
                this.hasAnimated = true;
                return;
            }

            if (!('IntersectionObserver' in window)) {
                this.animate();
                return;
            }

            this.observer = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (!entry.isIntersecting || this.hasAnimated) {
                        return;
                    }

                    this.animate();
                    this.observer?.disconnect();
                });
            }, {
                threshold: 0.4,
            });

            this.observer.observe(this.$el);
        },

        destroy() {
            this.observer?.disconnect();
        },

        animate() {
            if (this.hasAnimated) {
                return;
            }

            this.hasAnimated = true;

            const startTime = window.performance.now();
            const step = (now) => {
                const progress = Math.min(1, (now - startTime) / this.duration);
                const eased = 1 - Math.pow(1 - progress, 3);
                this.value = this.target * eased;

                if (progress < 1) {
                    window.requestAnimationFrame(step);
                    return;
                }

                this.value = this.target;
            };

            window.requestAnimationFrame(step);
        },

        format() {
            const formatter = new Intl.NumberFormat(document.documentElement.lang || 'en', {
                minimumFractionDigits: this.decimals,
                maximumFractionDigits: this.decimals,
            });

            return `${this.prefix}${formatter.format(this.value)}${this.suffix}`;
        },
    }));

    Alpine.data('faqItem', (initialState = false) => ({
        isOpen: Boolean(initialState),

        toggle() {
            this.isOpen = !this.isOpen;
        },
    }));

    Alpine.data('videoDialog', (videoUrl = '', title = 'SalesPartner video') => ({
        isOpen: false,
        videoUrl,
        title,

        open() {
            this.isOpen = true;
            bodyScrollLock.lock();
        },

        close() {
            this.isOpen = false;
            bodyScrollLock.unlock();
        },

        toggle() {
            if (this.isOpen) {
                this.close();
                return;
            }

            this.open();
        },
    }));

    Alpine.data('contactForm', () => ({
        isSubmitting: false,
        isValid: true,

        submit(event) {
            const form = this.$el;

            if (!(form instanceof HTMLFormElement)) {
                return;
            }

            this.isValid = form.checkValidity();

            if (!this.isValid) {
                event.preventDefault();
                const invalidField = form.querySelector(':invalid');

                if (invalidField instanceof HTMLElement) {
                    invalidField.focus();
                    invalidField.setAttribute('aria-invalid', 'true');
                }

                return;
            }

            this.isSubmitting = true;
        },

        resetFieldState(event) {
            const field = event.target;

            if (field instanceof HTMLInputElement || field instanceof HTMLTextAreaElement || field instanceof HTMLSelectElement) {
                field.removeAttribute('aria-invalid');
            }
        },
    }));
};

const registerGlobalEvents = () => {
    window.SalesPartnerFrontend = Object.freeze({
        refreshIcons,
        scrollToTarget,
        setTheme: (preference) => Alpine.store('theme').set(preference),
        toggleTheme: () => Alpine.store('theme').toggle(),
    });

    window.addEventListener('hashchange', () => {
        const hash = window.location.hash;

        if (!hash) {
            return;
        }

        const target = getAnchorTarget(hash);

        if (target instanceof HTMLElement) {
            scrollToTarget(target, {
                updateHistory: false,
                focusTarget: false,
            });
        }
    });

    window.addEventListener('error', (event) => {
        logger.error(event.error ?? event.message, 'Window error event');
    });

    window.addEventListener('unhandledrejection', (event) => {
        logger.error(event.reason, 'Unhandled promise rejection');
    });
};

const bootstrap = () => {
    Alpine.plugin(collapse);
    Alpine.plugin(focus);
    Alpine.plugin(persist);

    registerStores();
    registerComponents();
    registerGlobalEvents();

    window.Alpine = Alpine;
    Alpine.start();

    bootStaticEnhancements();
};

bootstrap();
